// A shared table of inbound call logs (Session ABT), used by the global Call log page and the
// customer detail Call log section. Renders date, caller (when showing the global list), duration,
// topic, outcome and whether an NPS survey was raised, with per-row Edit and two-step Delete. Pure
// presentation: the caller supplies the rows and the edit/delete handlers.
import { Link } from "react-router-dom";
import { ConfirmButton } from "./ConfirmButton";
import type { CallLogDto, RaisedWorkRef } from "../lib/api";

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function outcomePill(row: CallLogDto): { className: string } {
  switch (row.outcome) {
    case "complaint":
      return { className: "border-ele-danger text-ele-danger" };
    case "follow_up_needed":
    case "quote_requested":
      return { className: "border-ele-blue text-ele-blue" };
    case "resolved":
      return { className: "border-ele-green text-ele-green" };
    default:
      return { className: "border-ele-purple-accent text-ele-body" };
  }
}

// The plain-English NPS status for a row: waiting to be raised, raised, or none.
//
// Session AHY: this said "Sent", and after the survey became a Gmail draft the office would have
// read that word for a survey sitting unsent in the drafts folder. The platform genuinely cannot
// tell whether a draft was sent -- that fact lives in Gmail and nothing reports it back -- so the
// honest word is the one that covers both: the survey has been RAISED. The underlying npsSent field
// keeps its name; only what the office is told changed.
function npsLabel(row: CallLogDto): string {
  if (!row.npsQueued) return "No";
  return row.npsSent ? "Raised" : "Queued";
}

// Session ANV: where the office opens a piece of work raised from a call. A job has no detail page
// of its own in V2, so it is opened through the job sheet view under the established
// /office/jobsheets?job=:id convention (the same one the defect, invoice and customer pages use); a
// call-out lives on its own list page.
function raisedWorkHref(work: RaisedWorkRef): string {
  return work.kind === "callout" ? "/office/callouts" : `/office/jobsheets?job=${encodeURIComponent(work.id)}`;
}

// What the cell reads. The reference is the lift's ELE number (or the call-out's own), so the office
// recognises the row without opening it; where the record genuinely has neither -- a new customer's
// untagged lift, a lift-less call-out -- the type and status still say what happened.
export function raisedWorkLabel(work: RaisedWorkRef): string {
  return [work.typeLabel, work.reference, work.statusLabel].filter(Boolean).join(" · ");
}

export function CallLogTable({
  rows,
  showCaller,
  onEdit,
  onDelete,
  onRaiseWork,
  deletingId,
}: {
  rows: CallLogDto[];
  showCaller?: boolean; // show the caller/customer column (the global list); omitted on a customer page
  onEdit: (row: CallLogDto) => void;
  onDelete: (id: string) => void;
  // Session ANV: step 4 of the Director's workflow, "accept it as real work". Optional, so a host
  // that cannot raise work (nothing today, but the table is shared) simply does not offer it.
  onRaiseWork?: (row: CallLogDto) => void;
  deletingId?: string | null;
}) {
  if (rows.length === 0) {
    return <p className="py-6 text-sm text-ele-body">No calls logged.</p>;
  }
  return (
    <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
      <table className="ele-list-table w-full text-sm">
        <thead>
          <tr className="border-b border-ele-purple-accent font-menu text-xs uppercase tracking-wide text-ele-body">
            <th className="px-3 py-2 text-left">When</th>
            {showCaller && <th className="px-3 py-2 text-left">Caller</th>}
            {/* Duration/Topic/NPS hide below md: no detail page exists for a call log row, so
                mobile keeps When (+ Caller on the global list) and Outcome/Actions, which cover
                the two things office staff need on a phone (what happened, edit or delete it). */}
            <th className="max-md:hidden px-3 py-2 text-left">Duration</th>
            <th className="max-md:hidden px-3 py-2 text-left">Topic</th>
            <th className="px-3 py-2 text-left">Outcome</th>
            {/* Session ANV. Hidden below md alongside Duration/Topic/NPS, keeping the mobile
                layout the comment above describes intact: the RAISE action itself lives in the
                always-visible Actions cell, so a phone can still act on a call, it just reads the
                outcome of that act on a wider screen. */}
            <th className="max-md:hidden px-3 py-2 text-left">Work raised</th>
            <th className="max-md:hidden px-3 py-2 text-left">NPS</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ele-purple-accent">
          {rows.map((row) => (
            <tr key={row.id} className="text-ele-strong">
              <td className="px-3 py-2 align-top">
                {fmtDateTime(row.calledAt)}
                {row.source === "webhook" && (
                  <span
                    className="mt-1 block w-fit rounded-full border border-ele-blue px-2 py-0.5 font-menu text-[10px] uppercase tracking-wide text-ele-blue"
                    title="Logged automatically by a phone provider webhook"
                  >
                    Webhook
                  </span>
                )}
              </td>
              {showCaller && (
                <td className="px-3 py-2 align-top">{row.customerName ?? row.callerName ?? "Unknown caller"}</td>
              )}
              <td className="max-md:hidden px-3 py-2 align-top">{row.durationMinutes != null ? `${row.durationMinutes} min` : "-"}</td>
              <td className="max-md:hidden px-3 py-2 align-top text-ele-body">{row.topic ?? "-"}</td>
              <td className="px-3 py-2 align-top">
                <span className={`inline-block rounded-full border px-2 py-0.5 text-xs ${outcomePill(row).className}`}>{row.outcomeLabel}</span>
              </td>
              <td className="max-md:hidden px-3 py-2 align-top">
                {(row.raisedWork ?? []).length === 0 ? (
                  <span className="text-ele-body">-</span>
                ) : (
                  <ul>
                    {(row.raisedWork ?? []).map((w) => (
                      <li key={`${w.kind}-${w.id}`}>
                        <Link to={raisedWorkHref(w)} className="text-ele-green underline-offset-2 hover:underline">
                          {raisedWorkLabel(w)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </td>
              <td className="max-md:hidden px-3 py-2 align-top text-ele-body">{npsLabel(row)}</td>
              <td className="px-3 py-2 align-top text-right">
                <div className="flex justify-end gap-3">
                  {onRaiseWork && (
                    <button type="button" onClick={() => onRaiseWork(row)} className="font-menu text-xs tracking-wide text-ele-green">
                      Raise work
                    </button>
                  )}
                  <button type="button" onClick={() => onEdit(row)} className="font-menu text-xs tracking-wide text-ele-blue">Edit</button>
                  <ConfirmButton
                    onConfirm={() => onDelete(row.id)}
                    busy={deletingId === row.id}
                    className="font-menu text-xs tracking-wide text-ele-body"
                    title={`Delete the call logged on ${fmtDateTime(row.calledAt)}`}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
